import * as React from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { Card, useTheme, IconButton } from '@mui/material';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import LoadingSkeleton, { TabSkeleton } from '../notificationCard/LoadingSkeleton';
import { NotificationItemsType, NotificationList } from 'modules/notification/api/types';
import { useGetNotifications, useGetNotificationUnreadCount } from 'modules/notification/api/queries';
import { useMarkNotificationAsRead, useDeleteNotificationAll } from 'modules/notification/api/mutation';
import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog';
import NotificationCard from '../notificationCard';

// ---------------- Notification Item ----------------
const NotificationItem = ({ notification, handleRef }: { notification: NotificationList; handleRef: (el: Element | null, id: string | number) => void }) => {
    const { t } = useTranslation('notification');
    return (
    <Grid item>
        <div ref={(el) => handleRef(el, notification.id)}>
            <NotificationCard
                farm_name={notification.farm_id ? notification.farm_name : t('system_display')}
                title={notification.title}
                body={notification.body}
                created_at={notification.created_at}
                read_at={notification.read_at}
            />
        </div>
    </Grid>
    );
};

// ---------------- TabPanel ----------------
const NotificationTabPanel = ({ children, index, value }: { children: React.ReactNode; index: number; value: number }) => (
    <div role="tabpanel" hidden={value !== index}>{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}</div>
);

// ---------------- Main Component ----------------
export default function NotificationTab() {
    const { t } = useTranslation('notification');
    const theme = useTheme();
    const [value, setValue] = React.useState(0);

    const { data: unreadCount, isLoading: loadingUnread } = useGetNotificationUnreadCount();

    const tabs = React.useMemo(() => {
        const items = unreadCount?.items ?? [];
        const allUnread = unreadCount?.total_unread ?? items.reduce((s, it) => s + (it.unread ?? 0), 0);
        return [{ id: undefined as unknown as string | undefined, name: t('all'), unread: allUnread }, ...items];
    }, [unreadCount, t]);

    const selectedFarmId = React.useMemo(() => {
        const tab = tabs[value];
        if (!tab) return undefined;
        if ((tab as NotificationItemsType).name === 'system' || (tab as NotificationItemsType).id === 'system') return 'system';
        if (tab.id === undefined) return undefined;
        return tab.id && tab.id !== '0' ? tab.id : undefined;
    }, [tabs, value]);

    // ---------------- Notifications Query (infinite) ----------------
    const {
        data: notificationsData,
        isFetching,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useGetNotifications(selectedFarmId);
    const [readOverrides, setReadOverrides] = React.useState<Record<string, string>>({});

    const shouldIncludeNotification = React.useCallback((n: NotificationList) => {
        if (!n) return false;
        const title = (n.title ?? '').toString().trim();
        const body = (n.body ?? '').toString().trim();
        if (!title && !body) return false;
        if (((n as any).is_deleted) || ((n as any).deleted) || ((n as any).removed)) return false;
        return true;
    }, []);

    const notificationsState = React.useMemo(() => {
        if (!Array.isArray((notificationsData as any)?.data)) return [] as NotificationList[];
        const incoming: NotificationList[] = (notificationsData as any).data;
        return incoming.filter(shouldIncludeNotification);
    }, [notificationsData, shouldIncludeNotification]);

    // ---------------- Mark as read / Delete all ----------------
    const { mutateAsync: markAsReadMutation } = useMarkNotificationAsRead();
    const { mutateAsync: deleteAllMutation } = useDeleteNotificationAll();
    const { dialog, openDialog: openConfirmDialog } = useDeleteConfirmDialog({
        onConfirm: async () => {
            try {
                const deleteParam =
                    selectedFarmId === undefined
                        ? undefined
                        : selectedFarmId === 'system'
                            ? 'system'
                            : String(selectedFarmId);

                await deleteAllMutation(deleteParam as any);
                setReadOverrides({});
            } catch { }
        },
    });

    const markAsRead = React.useCallback(async (id: string | number) => {
        try { await markAsReadMutation([String(id)]); } catch { }
        setReadOverrides(prev => ({ ...prev, [String(id)]: new Date().toISOString() }));
    }, [markAsReadMutation]);

    // ---------------- Intersection Observer ----------------
    const observersRef = React.useRef(new Map<string | number, IntersectionObserver>());
    const timersRef = React.useRef(new Map<string | number, ReturnType<typeof setTimeout>>());
    const elementsRef = React.useRef(new Map<string | number, Element | null>());

    const handleRef = React.useCallback((el: Element | null, id: string | number) => {
        if (el) {
            elementsRef.current.set(id, el);
            if (!observersRef.current.has(id)) {
                const observer = new IntersectionObserver((entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            const timer = setTimeout(() => {
                                const n = notificationsState.find(x => x.id === id);
                                const effectiveReadAt = (n?.read_at as string | undefined) ?? readOverrides[String(id)];
                                if (n && !effectiveReadAt) markAsRead(id);
                                timersRef.current.delete(id);
                            }, 2000);
                            timersRef.current.set(id, timer);
                        } else {
                            const t = timersRef.current.get(id);
                            if (t) { clearTimeout(t); timersRef.current.delete(id); }
                        }
                    });
                }, { threshold: 0.5 });
                observersRef.current.set(id, observer);
                observer.observe(el);
            }
        } else {
            const obs = observersRef.current.get(id);
            if (obs) { obs.disconnect(); observersRef.current.delete(id); }
            const t = timersRef.current.get(id);
            if (t) { clearTimeout(t); timersRef.current.delete(id); }
            elementsRef.current.delete(id);
        }
    }, [markAsRead, notificationsState, readOverrides]);

    React.useEffect(() => () => {
        observersRef.current.forEach(o => o.disconnect());
        timersRef.current.forEach(t => clearTimeout(t));
        observersRef.current.clear();
        timersRef.current.clear();
        elementsRef.current.clear();
    }, []);

    // ---------------- Infinite Scroll ----------------
    const [sentryRef] = useInfiniteScroll({
        loading: isFetching || isFetchingNextPage,
        hasNextPage: !!hasNextPage,
        onLoadMore: () => { if (hasNextPage) fetchNextPage(); },
        disabled: false,
        rootMargin: '0px 0px 400px 0px',
    });

    const navigate = useNavigate();
    const location = useLocation();

    const getIndexFromFarmParam = React.useCallback((farmParam: string | null) => {
        if (farmParam === null) return 0;
        if (farmParam === 'system') {
            const idx = tabs.findIndex(tab => (tab as NotificationItemsType).id === 'system' || (tab as NotificationItemsType).name === 'system');
            return idx >= 0 ? idx : 0;
        }
        const idx = tabs.findIndex(tab => {
            const id = (tab as any).id;
            return id !== undefined && String(id) === String(farmParam);
        });
        return idx >= 0 ? idx : 0;
    }, [tabs]);

    React.useEffect(() => {
        const sp = new URLSearchParams(location.search);
        const farm = sp.get('farm');
        const idx = getIndexFromFarmParam(farm);
        if (idx !== value) {
            setValue(idx);
            setReadOverrides({});
        }
    }, [location.search, tabs, getIndexFromFarmParam, value]);

    const handleChange = (_: React.SyntheticEvent, newValue: number) => {
        const tab = tabs[newValue];
        let newSelectedFarmId: string | undefined;
        if (!tab) newSelectedFarmId = undefined;
        else if ((tab as NotificationItemsType).name === 'system' || (tab as NotificationItemsType).id === 'system') newSelectedFarmId = 'system';
        else if ((tab as any).id === undefined) newSelectedFarmId = undefined;
        else newSelectedFarmId = (tab as any).id && (tab as any).id !== '0' ? (tab as any).id : undefined;

        const sp = new URLSearchParams(location.search);
        if (newSelectedFarmId === undefined) sp.delete('farm');
        else sp.set('farm', String(newSelectedFarmId));
        const search = sp.toString();
        navigate(`${location.pathname}${search ? `?${search}` : ''}`);

        // รีเซ็ตสถานะที่ต้องการ
        setValue(newValue);
        setReadOverrides({});
    };

    const renderFlatNotifications = () => {
        if (!notificationsState.length) return null;
        return notificationsState.map(n => {
            const effectiveReadAt = (n.read_at as string | undefined) ?? readOverrides[String(n.id)];
            return <NotificationItem key={n.id} notification={{ ...n, read_at: effectiveReadAt } as NotificationList} handleRef={handleRef} />;
        });
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {loadingUnread ? (
                    <TabSkeleton count={8} />
                ) : (
                    <>
                        <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons="auto">
                            {tabs.map((tab, idx) => {
                                const count = (tab as NotificationItemsType).unread ?? 0;
                                const displayName = idx === 0
                                    ? tab.name
                                    : ((tab as NotificationItemsType).id ? tab.name : t('system_display'));
                                return <Tab key={(tab as NotificationItemsType).id ?? idx} label={`${displayName} (${count})`} />;
                            })}
                        </Tabs>
                    </>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={openConfirmDialog}>
                    <IconButton size="small"><Icon color={theme.palette.success.main} icon="mingcute:delete-2-line" width="24" height="24" /></IconButton>
                    <Typography color={theme.palette.success.main}>{t('delete_all')}</Typography>
                </Box>
            </Box>

            {(!isFetching && notificationsState.length === 0) ? (
                <Box
                    sx={{
                        height: '70vh',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <img src="/static/icons/ci_no_data.svg" alt="" />
                    <Typography color="text.secondary" variant="h6" sx={{ mt: 2 }}>
                        {t('no_notifications')}
                    </Typography>
                </Box>
            ) : (
                <Card sx={{ px: 4, py: 2, mt: 2 }}>
                    {tabs.map((tt, i) => (
                        <NotificationTabPanel key={tt.id ?? i} index={i} value={value}>
                            <Grid container direction="column" spacing={1}>
                                {renderFlatNotifications()}
                                {(isFetching || isFetchingNextPage) && <Grid item><LoadingSkeleton /></Grid>}
                                {value === i && <div ref={sentryRef} />}
                            </Grid>
                        </NotificationTabPanel>
                    ))}
                </Card>
            )}
            {dialog}
        </Box>
    );
}
